import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import Icon from 'react-native-vector-icons/Ionicons';

import { RootState, AppDispatch } from '@/store';
import { getEmployeeDirectory } from '@/store/slices/employeeSlice';
import { assignCall } from '@/store/slices/callReviewSlice';
import { colors, font, space, radius } from '@/theme/chalkCourt';

interface Props {
  visible: boolean;
  callUuid: string | null;
  /** Currently-assigned employee (for the check mark on reassign), if any. */
  currentEmployeeId?: number | null;
  onClose: () => void;
  onAssigned: (employeeName?: string) => void;
}

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

/**
 * Bottom-sheet staff picker for assigning a call to an employee (admin). Mirrors
 * SubPickerModal — the full active directory, searchable by name + department —
 * dispatching assignCall. The list is reconciled server-side.
 */
const AssigneePickerModal: React.FC<Props> = ({
  visible,
  callUuid,
  currentEmployeeId,
  onClose,
  onAssigned,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { directory, isLoadingDirectory, error } = useSelector(
    (state: RootState) => state.employee
  );
  const { assigningUuid } = useSelector((state: RootState) => state.callReview);
  const isAssigning = assigningUuid != null;

  const [query, setQuery] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setQuery('');
      setSubmitError(null);
      dispatch(getEmployeeDirectory());
    }
  }, [visible, dispatch]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return directory;
    }
    return directory.filter(
      (s) =>
        s.fullName.toLowerCase().includes(q) ||
        (s.departmentName ?? '').toLowerCase().includes(q)
    );
  }, [directory, query]);

  const pick = async (employeeId: number, fullName: string) => {
    if (!callUuid) {
      return;
    }
    setSubmitError(null);
    const result = await dispatch(assignCall({ callUuid, employeeId }));
    if (assignCall.fulfilled.match(result)) {
      onAssigned(fullName);
      onClose();
    } else {
      setSubmitError((result.payload as string) || 'Could not assign the call.');
    }
  };

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.sheet}>
          <View style={styles.handle} />
          <View style={styles.headRow}>
            <Text style={styles.title}>ASSIGN TO STAFF</Text>
            <TouchableOpacity
              onPress={onClose}
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              accessibilityLabel="Close assignee picker">
              <Icon name="close" size={24} color={colors.inkSoft} />
            </TouchableOpacity>
          </View>

          <View style={styles.searchBox}>
            <Icon name="search" size={18} color={colors.inkFaint} />
            <TextInput
              style={styles.searchInput}
              value={query}
              onChangeText={setQuery}
              placeholder="Search staff…"
              placeholderTextColor={colors.inkFaint}
              autoCapitalize="none"
            />
          </View>

          {submitError && <Text style={styles.submitError}>{submitError}</Text>}

          {isLoadingDirectory && directory.length === 0 ? (
            <View style={styles.centered}>
              <ActivityIndicator color={colors.accent} />
            </View>
          ) : error && directory.length === 0 ? (
            <View style={styles.centered}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          ) : (
            <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
              {filtered.length === 0 ? (
                <Text style={styles.noMatches}>No staff match "{query}".</Text>
              ) : (
                filtered.map((s) => {
                  const isCurrent = s.id === currentEmployeeId;
                  return (
                    <TouchableOpacity
                      key={s.id}
                      style={[styles.row, isCurrent && styles.rowCurrent]}
                      activeOpacity={0.7}
                      disabled={isAssigning}
                      onPress={() => pick(s.id, s.fullName)}>
                      <View style={styles.avatar}>
                        <Text style={styles.avatarText}>{initials(s.fullName)}</Text>
                      </View>
                      <View style={styles.rowBody}>
                        <Text style={styles.rowTitle} numberOfLines={1}>
                          {s.fullName}
                        </Text>
                        {!!s.departmentName && (
                          <Text style={styles.rowSubtitle}>{s.departmentName}</Text>
                        )}
                      </View>
                      {isCurrent && <Icon name="checkmark-circle" size={22} color={colors.court} />}
                    </TouchableOpacity>
                  );
                })
              )}
            </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(28,26,18,0.45)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: colors.bg,
    borderTopLeftRadius: radius.lg,
    borderTopRightRadius: radius.lg,
    paddingHorizontal: space.lg,
    paddingBottom: space.xl,
    height: '80%',
  },
  handle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.lineStrong,
    marginTop: space.sm,
    marginBottom: space.md,
  },
  headRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: space.md,
  },
  title: {
    fontFamily: font.display,
    fontSize: 26,
    color: colors.ink,
    letterSpacing: -0.5,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    paddingHorizontal: space.md,
    marginBottom: space.sm,
  },
  searchInput: {
    flex: 1,
    height: 44,
    fontFamily: font.body,
    fontSize: 15,
    color: colors.ink,
    marginLeft: space.sm,
  },
  submitError: {
    fontFamily: font.bodyMed,
    fontSize: 13,
    color: colors.accentDark,
    marginBottom: space.sm,
  },
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  errorText: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.inkSoft,
    padding: space.lg,
    textAlign: 'center',
  },
  list: { flex: 1 },
  listContent: { paddingTop: space.sm, paddingBottom: space.lg },
  noMatches: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.inkFaint,
    textAlign: 'center',
    paddingVertical: space.lg,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.sm,
    borderWidth: 1,
    borderColor: colors.line,
  },
  rowCurrent: {
    borderColor: colors.court,
    backgroundColor: colors.courtSoft,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.surfaceSunk,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: space.md,
  },
  avatarText: {
    fontFamily: font.bodyBold,
    fontSize: 14,
    color: colors.inkSoft,
  },
  rowBody: { flex: 1 },
  rowTitle: {
    fontFamily: font.bodyBold,
    fontSize: 15,
    color: colors.ink,
  },
  rowSubtitle: {
    fontFamily: font.bodyMed,
    fontSize: 12,
    color: colors.inkFaint,
    marginTop: 1,
  },
});

export default AssigneePickerModal;
